/**
 * ProjectionInterstitial.tsx — the projection + "building" pause beats.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: two pauses in the journey. The projection interstitial shows the
 * illustrative curve right after Step 0 (the aspirational hook — the chart
 * carries its own MANDATORY disclaimer); the building interstitial covers the
 * submit round-trip before the result. Both are pre-account public surfaces —
 * category-level copy only (R28). Continue is green (in-flow navigation), never
 * the terracotta conversion CTA.
 *
 * The building interstitial is a staged "We’re checking your answers" checklist
 * since 2026-09-24 (CTO — it was a spinner under "Matching you to the right
 * treatment"). WHICH step is current, and for how long, is decided by
 * `runBuildingBeat` in `journeyLoaders.ts`, where every step is tied to the real
 * work it describes and held long enough to read; this component only draws the
 * view it is handed.
 *
 * THE PROJECTION SCREEN HAS ONE <h1> AND NO COUNT (premium pass, 2026-09-24,
 * Build B). It had no heading at all — the line "What could be within reach"
 * was a 12px eyebrow — and a literal "1/13" step count in its bar, which the
 * journey retired everywhere else on 2026-09-11 (the progress line is a
 * phrase, never a count; `progressCopy.ts`). The same words are now the
 * screen's display heading (no copy change), and the count is gone; the bar
 * keeps its back control and its progress line.
 *
 * The headline loss number counts up from 0 on mount (requestAnimationFrame,
 * eased) in step with the chart's own line-draw reveal below it, so the
 * weight visibly "comes off" as the screen loads. Gated on the same
 * `usePrefersReducedMotion` signal as the chart — reduced-motion users see the
 * final number immediately.
 */
import { useEffect, useState } from 'react';
import { Button, Help } from '../ui/kit';
import { JourneyScreen } from './JourneyScreen';
import { NarratedChecklist } from './NarratedChecklist';
import { BUILDING_COPY, BUILDING_STEPS, type BuildingPhase } from './journeyLoaders';
import { ProjectionChart, usePrefersReducedMotion } from './ProjectionChart';
import { projectedLossKg } from './projection';

/** Duration of the headline count-up — matches the chart's line-draw reveal. */
const COUNT_UP_MS = 1300;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Counts 0 → `target` over {@link COUNT_UP_MS}, eased. Jumps straight to `target` when `disabled` (reduced motion / no rAF support). Cleans up its frame on unmount or restart. */
function useCountUp(target: number, disabled: boolean): number {
  const [value, setValue] = useState(() => (disabled ? target : 0));

  useEffect(() => {
    if (disabled || typeof requestAnimationFrame !== 'function') {
      setValue(target);
      return;
    }
    setValue(0);
    let frameId = 0;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / COUNT_UP_MS);
      setValue(target * easeOutCubic(t));
      if (t < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [target, disabled]);

  return value;
}

/** Projection interstitial: the loss headline + curve + Continue. */
export function ProjectionInterstitial({
  weightKg,
  percent,
  onBack,
  onContinue,
}: {
  weightKg: number;
  percent: number;
  onBack: () => void;
  onContinue: () => void;
}) {
  const loss = projectedLossKg(weightKg);
  const reduced = usePrefersReducedMotion();
  const displayedLoss = Math.round(useCountUp(loss, reduced));
  return (
    <JourneyScreen
      onBack={onBack}
      percent={percent}
      action={
        <Button type="button" variant="primary" onClick={onContinue}>
          Continue
        </Button>
      }
    >
      <div style={{ textAlign: 'center' }}>
        {/* The screen's ONE heading — the words were this screen's eyebrow; the
            styling is `.gps-jux-projection-title` (tokens.css, P9). */}
        <h1 className="gps-jux-projection-title">What could be within reach</h1>
        {/* The figure's type (a light serif, green-text 7.2:1 on ivory) is the
            class — tokens.css P9. */}
        <div
          className="gps-jux-projection-figure"
          style={{
            margin: '2px 0',
            // A brief scale settle timed to land as the count-up finishes
            // (delay ≈ the count-up duration). Reduced-motion → the global kill
            // neutralises it (and there is no count-up to settle anyway) (E3 #7).
            // A leaf: the figure wraps nothing but its own text.
            animation: reduced ? undefined : `gps-settle var(--gps-dur) var(--gps-ease) ${COUNT_UP_MS}ms both`,
          }}
        >
          −{displayedLoss} kg
        </div>
        <Help style={{ marginBottom: 'var(--gps-s-3)' }}>Based on an average ~21% over 10 months</Help>
      </div>
      <div className="gps-jux-moment-card">
        <ProjectionChart weightKg={weightKg} />
      </div>
    </JourneyScreen>
  );
}

/**
 * "We’re checking your answers" — the staged checklist shown while the
 * submission is in flight (see `journeyLoaders.ts` for what each step means,
 * when it may tick and how long each is held).
 *
 * THE LOOK, THE MOTION AND THE ACCESSIBILITY RULES live in the shared
 * `NarratedChecklist` since 2026-09-25, when Step 0's capture loader was built
 * in the same visual language — one renderer, so the two waits cannot drift
 * apart. This screen supplies its words: the "Thank you" eyebrow, the heading,
 * the three steps, the intro and closing lines, and the still-working line
 * while the answer is late. The DOM is the one this component always drew
 * (`buildingChecklist.spec.tsx` pins it).
 *
 * ⚠️ NEUTRAL FOR BOTH OUTCOMES. The not-eligible group sees this screen too
 * (CLIN-02), and the verdict comes from an automated rules engine — so nothing
 * here may assume a plan, promise a treatment or mention a reviewing clinician.
 */
export function BuildingInterstitial({ phase, stillWorking = false }: { phase: BuildingPhase; stillWorking?: boolean }) {
  return (
    <JourneyScreen>
      <NarratedChecklist
        eyebrow="Thank you"
        title="We’re checking your answers"
        intro={BUILDING_COPY.intro}
        allDoneLine={BUILDING_COPY.allDone}
        steps={BUILDING_STEPS}
        phase={phase}
        waitLine={stillWorking ? BUILDING_COPY.stillWorking : null}
      />
    </JourneyScreen>
  );
}
